import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Input, Spin } from "antd";
import { EnvironmentOutlined, SearchOutlined } from "@ant-design/icons";

type LeafletLatLng = {
  lat: number;
  lng: number;
};

type LeafletMarker = {
  addTo: (map: LeafletMap) => LeafletMarker;
  setLatLng: (latLng: [number, number]) => LeafletMarker;
  on: (event: string, callback: () => void) => LeafletMarker;
  getLatLng: () => LeafletLatLng;
};

type LeafletMap = {
  remove: () => void;
  setView: (center: [number, number], zoom?: number) => LeafletMap;
  on: (event: string, callback: (event: { latlng: LeafletLatLng }) => void) => LeafletMap;
};

type LeafletApi = {
  map: (element: HTMLDivElement) => LeafletMap;
  tileLayer: (
    url: string,
    options: { attribution: string; maxZoom: number }
  ) => { addTo: (map: LeafletMap) => void };
  marker: (
    latLng: [number, number],
    options?: { draggable?: boolean }
  ) => LeafletMarker;
};

declare global {
  interface Window {
    L?: LeafletApi;
  }
}

type SearchResult = {
  display_name: string;
  lat: string;
  lon: string;
  address?: Record<string, string>;
};

export type MarketLocationValue = {
  address?: string;
  city?: string;
  state?: string;
  lat?: number | null;
  lng?: number | null;
};

export type ExtractedAddressInfo = {
  address: string;
  city: string;
  state: string;
};

export const extractAddressDetails = (data: unknown): ExtractedAddressInfo => {
  if (!data || typeof data !== "object") {
    return { address: "", city: "", state: "" };
  }

  const res = data as {
    display_name?: string;
    address?: Record<string, string>;
  };

  const fullAddress = typeof res.display_name === "string" ? res.display_name : "";
  const addr = res.address || {};

  let rawCity =
    addr.city ||
    addr.town ||
    addr.municipality ||
    addr.village ||
    addr.suburb ||
    addr.city_district ||
    addr.district ||
    addr.county ||
    addr.state_district ||
    "";

  let rawState = addr.state || addr.region || addr.state_district || "";

  if ((!rawCity || !rawState) && fullAddress) {
    const parts = fullAddress.split(",").map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 3) {
      const countryIndex = parts.findIndex(
        (p) => p.toLowerCase() === "nigeria" || p.toLowerCase() === "ng"
      );

      let stateIdx = -1;
      let cityIdx = -1;

      if (countryIndex > 0) {
        stateIdx = countryIndex - 1;
        cityIdx = countryIndex - 2;
      } else {
        stateIdx = parts.length - 1;
        cityIdx = parts.length - 2;
      }

      if (!rawState && stateIdx >= 0 && parts[stateIdx]) {
        rawState = parts[stateIdx];
      }
      if (!rawCity && cityIdx >= 0 && parts[cityIdx]) {
        rawCity = parts[cityIdx];
      }
    } else if (parts.length === 2) {
      if (!rawState) rawState = parts[1];
      if (!rawCity) rawCity = parts[0];
    }
  }

  let cleanedState = rawState.replace(/\s+State$/i, "").trim();
  let cleanedCity = rawCity.trim();

  return {
    address: fullAddress,
    city: cleanedCity,
    state: cleanedState,
  };
};

export type MarketLocationPickerProps = {
  value: MarketLocationValue;
  mode: "add" | "edit";
  onChange: (value: MarketLocationValue) => void;
};

const LEAFLET_CSS_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_SCRIPT_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const DEFAULT_CENTER: [number, number] = [9.082, 8.6753];
const DEFAULT_ZOOM = 6;
const SELECTED_ZOOM = 15;

let leafletPromise: Promise<LeafletApi> | null = null;

const loadLeaflet = () => {
  if (window.L) return Promise.resolve(window.L);

  if (!leafletPromise) {
    leafletPromise = new Promise((resolve, reject) => {
      if (!document.querySelector(`link[href="${LEAFLET_CSS_URL}"]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = LEAFLET_CSS_URL;
        document.head.appendChild(link);
      }

      const existingScript = document.querySelector<HTMLScriptElement>(
        `script[src="${LEAFLET_SCRIPT_URL}"]`
      );

      if (existingScript) {
        existingScript.addEventListener("load", () => {
          window.L ? resolve(window.L) : reject(new Error("Map failed to load."));
        });
        existingScript.addEventListener("error", () =>
          reject(new Error("Map failed to load."))
        );
        return;
      }

      const script = document.createElement("script");
      script.src = LEAFLET_SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        window.L ? resolve(window.L) : reject(new Error("Map failed to load."));
      };
      script.onerror = () => reject(new Error("Map failed to load."));
      document.body.appendChild(script);
    });
  }

  return leafletPromise;
};

export const MarketLocationPicker: React.FC<MarketLocationPickerProps> = ({
  value,
  mode,
  onChange,
}) => {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markerRef = useRef<LeafletMarker | null>(null);
  const leafletRef = useRef<LeafletApi | null>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState(value.address || "");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [geocoding, setGeocoding] = useState(false);

  const hasCoordinates =
    typeof value.lat === "number" &&
    Number.isFinite(value.lat) &&
    typeof value.lng === "number" &&
    Number.isFinite(value.lng);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const selectLocation = useCallback(
    async (
      lat: number,
      lng: number,
      address?: string,
      city?: string,
      state?: string,
      shouldReverseGeocode = true
    ) => {
      const currentValue = valueRef.current;
      const nextValue: MarketLocationValue = {
        address: address || currentValue.address || "",
        city: city || currentValue.city || "",
        state: state || currentValue.state || "",
        lat,
        lng,
      };

      if (leafletRef.current && mapRef.current) {
        if (!markerRef.current) {
          markerRef.current = leafletRef.current
            .marker([lat, lng], { draggable: true })
            .addTo(mapRef.current)
            .on("dragend", () => {
              const nextLatLng = markerRef.current?.getLatLng();
              if (nextLatLng) {
                selectLocation(nextLatLng.lat, nextLatLng.lng);
              }
            });
        } else {
          markerRef.current.setLatLng([lat, lng]);
        }

        mapRef.current.setView([lat, lng], SELECTED_ZOOM);
      }

      if ((address && city && state) || !shouldReverseGeocode) {
        onChangeRef.current(nextValue);
        if (address) setSearchText(address);
        return;
      }

      setGeocoding(true);

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
            lat
          )}&lon=${encodeURIComponent(lng)}`
        );
        const data = await response.json();
        const extracted = extractAddressDetails(data);

        const finalValue: MarketLocationValue = {
          address: extracted.address || nextValue.address,
          city: extracted.city || nextValue.city,
          state: extracted.state || nextValue.state,
          lat,
          lng,
        };

        onChangeRef.current(finalValue);
        if (finalValue.address) setSearchText(finalValue.address);
      } catch {
        onChangeRef.current(nextValue);
      } finally {
        setGeocoding(false);
      }
    },
    []
  );

  useEffect(() => {
    setSearchText(value.address || "");
  }, [value.address]);

  useEffect(() => {
    let mounted = true;

    loadLeaflet()
      .then((leaflet) => {
        if (!mounted || !mapElementRef.current || mapRef.current) return;

        leafletRef.current = leaflet;
        const currentValue = valueRef.current;
        const initialHasCoordinates =
          typeof currentValue.lat === "number" &&
          Number.isFinite(currentValue.lat) &&
          typeof currentValue.lng === "number" &&
          Number.isFinite(currentValue.lng);
        const center: [number, number] = initialHasCoordinates
          ? [currentValue.lat as number, currentValue.lng as number]
          : DEFAULT_CENTER;
        const map = leaflet.map(mapElementRef.current).setView(
          center,
          initialHasCoordinates ? SELECTED_ZOOM : DEFAULT_ZOOM
        );

        leaflet
          .tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: "&copy; OpenStreetMap contributors",
            maxZoom: 19,
          })
          .addTo(map);

        map.on("click", (event) => {
          selectLocation(event.latlng.lat, event.latlng.lng);
        });

        mapRef.current = map;

        if (initialHasCoordinates) {
          selectLocation(
            currentValue.lat as number,
            currentValue.lng as number,
            currentValue.address,
            currentValue.city,
            currentValue.state,
            false
          );
        }
      })
      .catch(() => {
        if (mounted) {
          setMapError("Unable to load map. Please try again.");
        }
      })
      .finally(() => {
        if (mounted) setMapLoading(false);
      });

    return () => {
      mounted = false;
      mapRef.current?.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [selectLocation]);

  useEffect(() => {
    if (!hasCoordinates || !mapRef.current || !leafletRef.current) return;
    if (markerRef.current) {
      markerRef.current.setLatLng([value.lat as number, value.lng as number]);
    }
  }, [hasCoordinates, value.lat, value.lng]);

  const searchLocation = async () => {
    const query = searchText.trim();
    if (!query) return;

    setSearching(true);
    setSearchResults([]);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=5&countrycodes=ng&q=${encodeURIComponent(
          query
        )}`
      );
      const results = (await response.json()) as SearchResult[];
      setSearchResults(Array.isArray(results) ? results : []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="market-location-picker">
      <div className="market-location-picker-head">
        <div>
          <div className="market-location-picker-title">
            Market Location & Map Selection
          </div>
          <div className="market-location-picker-subtitle">
            Search or click the map, then drag the marker to set the market position.
          </div>
        </div>
        {geocoding && <Spin size="small" />}
      </div>

      {mode === "edit" && !hasCoordinates && (
        <Alert
          type="warning"
          showIcon
          className="market-location-picker-alert"
          message="Location coordinates not available. Please select location on map."
        />
      )}

      <Input.Search
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        onSearch={searchLocation}
        enterButton={
          <Button type="primary" icon={<SearchOutlined />}>
            Search
          </Button>
        }
        loading={searching}
        placeholder="Search market, area, or address in Nigeria"
        size="large"
        className="market-location-search"
      />

      {searchResults.length > 0 && (
        <div className="market-location-results">
          {searchResults.map((result) => (
            <button
              type="button"
              key={`${result.lat}-${result.lon}-${result.display_name}`}
              className="market-location-result"
              onClick={() => {
                const lat = Number(result.lat);
                const lng = Number(result.lon);
                const extracted = extractAddressDetails(result);
                setSearchResults([]);
                selectLocation(
                  lat,
                  lng,
                  extracted.address || result.display_name,
                  extracted.city,
                  extracted.state,
                  false
                );
              }}
            >
              <EnvironmentOutlined />
              <span>{result.display_name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="market-location-map-wrap">
        {mapLoading && (
          <div className="market-location-map-state">
            <Spin />
          </div>
        )}
        {mapError ? (
          <div className="market-location-map-state">
            <Alert type="error" showIcon message={mapError} />
          </div>
        ) : (
          <div ref={mapElementRef} className="market-location-map" />
        )}
      </div>
    </div>
  );
};

export default MarketLocationPicker;
